
import React from 'react';
import { PhaseStatus, PhaseStatusIndicator } from './PhaseStatusIndicator';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';

export type Phase = {
  number: number;
  status: PhaseStatus;
  title: string;
  description: string;
};

interface MultiPhaseTrackerProps {
  phases: Phase[];
  currentPhase: number;
  className?: string;
  showOnlyCurrent?: boolean;
}

export const MultiPhaseTracker: React.FC<MultiPhaseTrackerProps> = ({
  phases,
  currentPhase,
  className = "",
  showOnlyCurrent = true
}) => {
  // Filter phases to show only the current one if showOnlyCurrent is true
  const displayPhases = showOnlyCurrent 
    ? phases.filter(phase => phase.number === currentPhase)
    : phases;
  
  // Calculate the overall progress percentage
  const getTotalProgress = () => {
    const totalPhases = phases.length;
    const completedPhases = phases.filter(phase => 
      phase.status === 'completed'
    ).length;
    
    const inProgressPhase = phases.find(phase => phase.status === 'in-progress');
    // Add 0.5 for in-progress phase
    const progressValue = completedPhases + (inProgressPhase ? 0.5 : 0);
    
    return (progressValue / totalPhases) * 100;
  };
  
  // Get the current phase label for the phase step indicator
  const getCurrentPhaseLabel = () => {
    const currentPhaseObj = phases.find(phase => phase.number === currentPhase);
    if (!currentPhaseObj) return 'Unknown Phase';
    
    return `${currentPhaseObj.title} (${getStatusLabel(currentPhaseObj.status)})`;
  };
  
  // Helper to get readable status label
  const getStatusLabel = (status: PhaseStatus): string => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'pending': return 'Pending';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };
    
  return (
    <div className={`space-y-4 ${className}`}>
      <h3 className="text-lg font-semibold text-center">
        {showOnlyCurrent ? 'Current Phase' : 'Onboarding Progress'}
      </h3>
      
      {!showOnlyCurrent && (
        <div className="mb-3">
          <Progress value={getTotalProgress()} className="h-2" />
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>Application</span>
            <span>Review</span>
            <span>Invoice</span>
            <span>Payment</span>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-3">
        {displayPhases.map((phase) => (
          <div 
            key={phase.number}
            className={`p-3 rounded-lg border ${
              phase.number === currentPhase
                ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <PhaseStatusIndicator 
                status={phase.status} 
                phase={phase.number} 
                description={phase.description} 
              />
              <span className="text-sm font-medium min-w-[120px] min-h-[40px] flex items-center">{phase.title}</span>
            </div>
            <Separator className="my-2" />
            <p className="text-xs text-gray-600 dark:text-gray-400">{phase.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
